import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { DmPolicy } from "@townsy/contracts";
import { errorMessage } from "../../api/client";
import { messageKeys, putDmSettings, useDmSettingsQuery } from "../../api/messages";
import { useTownStore } from "../../state/store";

/** Account page (ADR-0014): who may send you private messages. Shown when the server has private messages on. */
export function DmSettingsCard({ signedIn, enabled }: { signedIn: boolean; enabled: boolean }) {
  const q = useDmSettingsQuery(signedIn && enabled);
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** The choice being saved: shown checked at once (a controlled radio would otherwise snap back until the server answers). */
  const [pending, setPending] = useState<DmPolicy | null>(null);
  if (!signedIn || !enabled || !q.data) return null;
  const current = pending ?? q.data.policy;
  const choose = async (policy: DmPolicy) => {
    if (policy === current) return;
    setPending(policy);
    setBusy(true);
    setError(null);
    try {
      const res = await putDmSettings(policy);
      qc.setQueryData(messageKeys.settings, res);
      useTownStore.getState().pushToast(policy === "nobody" ? "Nobody can send you private messages now." : "People with a verified account can send you private messages.", "success");
    } catch (err) {
      setError(errorMessage(err, "The setting was not saved."));
    } finally {
      setPending(null);
      setBusy(false);
    }
  };
  return (
    <div className="section" data-testid="account-dm-settings">
      <div className="section-title">Private messages</div>
      <fieldset className="choice-group" disabled={busy}>
        <legend className="small">Who can send you private messages?</legend>
        <label className="choice">
          <input type="radio" name="dm-policy" checked={current === "everyone"} onChange={() => void choose("everyone")} data-testid="dm-policy-everyone" />
          <span>Anyone with a verified Townsy account</span>
        </label>
        <label className="choice">
          <input type="radio" name="dm-policy" checked={current === "nobody"} onChange={() => void choose("nobody")} data-testid="dm-policy-nobody" />
          <span>Nobody</span>
        </label>
      </fieldset>
      <span className="hint">Muting someone also stops their private messages. Moderators read a conversation only if someone reports it.</span>
      {error ? (
        <div className="small error-text" role="alert">
          {error}
        </div>
      ) : null}
    </div>
  );
}
